/**
 * workdayRules.js
 *
 * Pure rules of the "Munkanap" (workday clock-in / clock-out) feature: storage keys, elapsed-time maths with server
 * clock-skew compensation, the reminder / snooze rules, the start-prompt decision and the modal sequencing rule.
 * No React / storage / network imports — Jest-safe. State fetching lives in {@link module:helpers/workdayApi}, the
 * provider in {@link module:helpers/WorkdayContext}.
 *
 * @module helpers/workdayRules
 */

import {BRIEFING_BLOCKED_ROUTES, isRouteAllowed} from './myDayBriefing';

export {BRIEFING_BLOCKED_ROUTES, isRouteAllowed};

/** Default daily target in minutes (8 h) when the server does not say otherwise. */
export const DEFAULT_TARGET_MINUTES = 480;

/** Snooze choices (minutes) the backend accepts. */
export const SNOOZE_OPTIONS = Object.freeze([15, 30, 60]);

/** Past-target thresholds: amber right after the target, red one hour later. */
export const OVERTIME_DANGER_SEC = 60 * 60;

/** Identifier prefix of the pending local reminder notification. */
export const reminderIdentifier = (userId) => `workday:reminder:${userId}`;

/** @param {string} userId user id. @param {string} day local day key. @returns {string} "start prompt dismissed today" key. */
export const startDismissKey = (userId, day) => `workday:startDismissed:${userId}:${day}`;

/**
 * Local calendar day key `YYYY-MM-DD`.
 * @param {Date|number} [date] reference.
 * @returns {string} day key.
 */
export const dayKeyOf = (date = new Date()) => {
    const d = date instanceof Date ? date : new Date(date);
    const p = (n) => String(n).padStart(2, '0');

    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const toMs = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const ms = typeof value === 'number' ? value : new Date(value).getTime();

    return Number.isFinite(ms) ? ms : null;
};

/**
 * Whether `minutes` is one of the accepted snooze presets.
 * @param {number|string} minutes minutes.
 * @returns {boolean} true when valid.
 */
export const isValidSnooze = (minutes) => SNOOZE_OPTIONS.includes(Number(minutes));

/**
 * Seconds of the running session at `now`, skew-proof: the server reports `elapsedSec` at its own `serverNow`; we add the
 * time that passed on OUR clock since the response arrived (`fetchedAt`), so a wrong device clock never shifts the timer.
 * Falls back to `startedAt` + the `serverNow - fetchedAt` skew when `elapsedSec` is missing.
 * @param {{running: object|null, elapsedSec?: number, serverNow?: string|number, fetchedAt?: number}} state normalized state.
 * @param {number} [now] client epoch ms.
 * @returns {number} elapsed seconds (>= 0), 0 when nothing runs.
 */
export const liveElapsedSec = (state, now = Date.now()) => {
    const running = state?.running;

    if (!running) {
        return 0;
    }

    const fetchedAt = Number.isFinite(state.fetchedAt) ? state.fetchedAt : now;
    // A PAUSED workday is a frozen total: only the break grows, the worked time never ticks.
    const sinceFetch = isPausedState(state) ? 0 : Math.max(0, (now - fetchedAt) / 1000);
    const base = Number(state.elapsedSec);

    if (Number.isFinite(base) && base >= 0) {
        return Math.floor(base + sinceFetch);
    }

    const startedAt = toMs(running.startedAt);

    if (startedAt === null) {
        return 0;
    }

    const serverNow = toMs(state.serverNow);
    const skew = serverNow === null ? 0 : serverNow - fetchedAt;
    const wall = Math.max(0, Math.floor((now + skew - startedAt) / 1000));

    return Math.max(0, wall - liveBreakSec(state, now));
};

/**
 * Whether the state's running workday is paused (a break is open).
 * @param {{paused?: boolean, running?: {paused?: boolean}|null}} state normalized state.
 * @returns {boolean} true while paused.
 */
export const isPausedState = (state) => !!state?.running && (state.paused === true || state.running.paused === true);

/**
 * Break seconds of the running session at `now` (closed breaks + the open one while paused), skew-proof like
 * {@link liveElapsedSec}: the server's `breakSec` at `serverNow` plus our own time since receipt while paused.
 * @param {object} state normalized state.
 * @param {number} [now] client epoch ms.
 * @returns {number} break seconds (>= 0), 0 when nothing runs.
 */
export const liveBreakSec = (state, now = Date.now()) => {
    if (!state?.running) {
        return 0;
    }

    const fetchedAt = Number.isFinite(state.fetchedAt) ? state.fetchedAt : now;
    const base = Math.max(0, Number(state.breakSec) || Number(state.running.breakSec) || 0);

    return Math.floor(base + (isPausedState(state) ? Math.max(0, (now - fetchedAt) / 1000) : 0));
};

/**
 * Seconds worked today: the closed sessions' durations plus the live running one. Computed from `sessions` so it is
 * self-consistent whether or not the server's `workedSec` already contains the running session; `workedSec` is only the
 * fallback when no session list came back.
 * @param {object} state normalized state.
 * @param {number} [now] client epoch ms.
 * @returns {number} seconds.
 */
export const workedTodaySec = (state, now = Date.now()) => {
    const sessions = Array.isArray(state?.today?.sessions) ? state.today.sessions : [];

    if (!sessions.length) {
        const live = state?.running ? liveElapsedSec(state, now) : 0;

        return Math.max(Number(state?.today?.workedSec) || 0, live);
    }

    const closed = sessions.reduce((sum, s) => {
        const a = toMs(s?.startedAt);
        const b = toMs(s?.endedAt);

        return a !== null && b !== null && b > a ? sum + Math.max(0, Math.floor((b - a) / 1000) - Math.max(0, Number(s?.breakSec) || 0)) : sum;
    }, 0);

    return closed + liveElapsedSec(state, now);
};

/**
 * Target of the day in seconds.
 * @param {{settings?: {targetMinutes?: number}, running?: {targetMinutes?: number}|null}} state state.
 * @returns {number} target seconds (> 0).
 */
export const targetSec = (state) => {
    const minutes = Number(state?.running?.targetMinutes) || Number(state?.settings?.targetMinutes) || DEFAULT_TARGET_MINUTES;

    return Math.max(60, minutes * 60);
};

/**
 * @param {number} workedSec worked seconds. @param {number} target target seconds.
 * @returns {{progress: number, remainingSec: number, overtimeSec: number, tone: 'ok'|'warn'|'danger'}} ring model; `progress`
 *   is clamped to 0..1, `tone` goes amber past the target and red one hour later.
 */
export const progressModel = (workedSec, target) => {
    const worked = Math.max(0, Number(workedSec) || 0);
    const tgt = Math.max(1, Number(target) || DEFAULT_TARGET_MINUTES * 60);
    const overtimeSec = Math.max(0, worked - tgt);

    return {
        progress: Math.min(1, worked / tgt),
        remainingSec: Math.max(0, tgt - worked),
        overtimeSec,
        tone: overtimeSec >= OVERTIME_DANGER_SEC ? 'danger' : (worked >= tgt ? 'warn' : 'ok'),
    };
};

/**
 * `HH:MM` (hours may exceed 24 never; capped display at 99).
 * @param {number} sec seconds. @returns {string} text.
 */
export const formatHM = (sec) => {
    const total = Math.max(0, Math.floor(Number(sec) || 0));
    const h = Math.min(99, Math.floor(total / 3600));
    const m = Math.floor((total % 3600) / 60);

    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/**
 * `H:MM:SS` for large live counters.
 * @param {number} sec seconds. @returns {string} text.
 */
export const formatHMS = (sec) => {
    const total = Math.max(0, Math.floor(Number(sec) || 0));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;

    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

/**
 * Whether the end-of-workday reminder is due: the server says so, or the running session's `reminderAt` has passed on the
 * skew-corrected clock.
 * @param {object} state normalized state.
 * @param {number} [now] client epoch ms.
 * @returns {boolean} true when the reminder should be shown.
 */
export const isReminderDue = (state, now = Date.now()) => {
    if (!state?.running) {
        return false;
    }

    if (state.reminderDue === true) {
        return true;
    }

    const at = toMs(state.running.reminderAt);

    if (at === null) {
        return false;
    }

    const serverNow = toMs(state.serverNow);
    const fetchedAt = Number.isFinite(state.fetchedAt) ? state.fetchedAt : now;
    const skew = serverNow === null ? 0 : serverNow - fetchedAt;

    return now + skew >= at;
};

/**
 * Local-notification plan for the reminder: schedule at the session's `reminderAt` (translated to the device clock), cancel
 * when nothing runs or the time already passed.
 * @param {object} state normalized state.
 * @param {number} [now] client epoch ms.
 * @returns {{action: 'schedule'|'cancel', at?: number}} plan.
 */
export const reminderPlan = (state, now = Date.now()) => {
    const at = toMs(state?.running?.reminderAt);

    if (!state?.running || at === null) {
        return {action: 'cancel'};
    }

    const serverNow = toMs(state.serverNow);
    const fetchedAt = Number.isFinite(state.fetchedAt) ? state.fetchedAt : now;
    const skew = serverNow === null ? 0 : serverNow - fetchedAt;
    const deviceAt = at - skew;

    return deviceAt > now + 1000 ? {action: 'schedule', at: deviceAt} : {action: 'cancel'};
};

/**
 * Whether the start-of-day prompt should be offered.
 * @param {object} s inputs.
 * @param {boolean} s.ready logged in, navigator ready, route allowed.
 * @param {boolean} s.supported the backend supports the feature (a 404/405 hides it).
 * @param {boolean} s.loaded the first state fetch finished.
 * @param {boolean} s.running a session is running.
 * @param {boolean} s.startedToday a session was already started today (even if ended).
 * @param {boolean} s.dismissedToday the user dismissed the prompt today.
 * @returns {{show: boolean, reason: string}} decision; `reason`: not-ready | unsupported | loading | running | started-today | dismissed | prompt.
 */
export const resolveStartPrompt = ({ready, supported, loaded, running, startedToday, dismissedToday} = {}) => {
    if (!ready) {
        return {show: false, reason: 'not-ready'};
    }

    if (!supported) {
        return {show: false, reason: 'unsupported'};
    }

    if (!loaded) {
        return {show: false, reason: 'loading'};
    }

    if (running) {
        return {show: false, reason: 'running'};
    }

    if (startedToday) {
        return {show: false, reason: 'started-today'};
    }

    if (dismissedToday) {
        return {show: false, reason: 'dismissed'};
    }

    return {show: true, reason: 'prompt'};
};

/**
 * Who hosts the start prompt, given the "Mai napom" briefing's auto-open decision reason
 * ({@link module:helpers/myDayBriefing.resolveAutoOpen}):
 * - the briefing opens now -> `briefing` (its Munkanap hero card IS the prompt; no standalone sheet),
 * - the briefing will not open today (`skipped` / `seen`) -> `standalone` (compact WorkdayStartSheet),
 * - the briefing is merely delayed (`snoozed`) or not decided yet (`not-ready`) -> `wait`.
 * @param {{open?: boolean, reason?: string}|null} decision briefing decision, or null when none was made yet.
 * @returns {'briefing'|'standalone'|'wait'} host.
 */
export const startPromptHost = (decision) => {
    if (!decision) {
        return 'wait';
    }

    if (decision.open) {
        return 'briefing';
    }

    return decision.reason === 'skipped' || decision.reason === 'seen' ? 'standalone' : 'wait';
};

/**
 * The ONE surface allowed on screen at a time (never two modals at once). Priority: the briefing (it embeds the workday
 * card and the reminder state) > the user-opened status sheet > the end-of-day reminder > the standalone start sheet.
 * @param {object} s inputs.
 * @param {boolean} s.briefingVisible the "Mai napom" briefing is open.
 * @param {boolean} [s.sheetOpen] the user opened the status sheet from the header pill.
 * @param {boolean} s.reminderWanted the reminder modal was requested / is due and not snoozed.
 * @param {boolean} s.startWanted the standalone start sheet is wanted.
 * @param {boolean} s.routeAllowed current route allows overlays (not login / lock / 2FA / start).
 * @returns {'none'|'briefing'|'sheet'|'reminder'|'start'} surface.
 */
export const pickSurface = ({briefingVisible, sheetOpen = false, reminderWanted, startWanted, routeAllowed = true} = {}) => {
    if (briefingVisible) {
        return 'briefing';
    }

    if (sheetOpen && routeAllowed) {
        return 'sheet';
    }

    if (!routeAllowed) {
        return 'none';
    }

    if (reminderWanted) {
        return 'reminder';
    }

    return startWanted ? 'start' : 'none';
};

/** Minimum gap between probes of `/workday/current` while the backend reports the feature unsupported (404/405). */
export const UNSUPPORTED_POLL_MS = 10 * 60 * 1000;

/**
 * Whether a state poll/foreground refresh may run now. While the feature is unsupported it is throttled to
 * {@link UNSUPPORTED_POLL_MS}; otherwise always allowed.
 * @param {boolean} supported current support flag.
 * @param {number} lastAt epoch ms of the last request (0 = never).
 * @param {number} [now] epoch ms.
 * @returns {boolean} true when a request may be sent.
 */
export const shouldRefresh = (supported, lastAt, now = Date.now()) => supported || !(lastAt > 0) || now - lastAt >= UNSUPPORTED_POLL_MS;

/**
 * Whether the local day changed since `prevKey` (session-level "skip today" flags must then reset).
 * @param {string} prevKey last evaluated day key. @param {string} nowKey current day key.
 * @returns {boolean} true when the day rolled over.
 */
export const dayRolledOver = (prevKey, nowKey) => Boolean(prevKey) && prevKey !== nowKey;
